
import {
  defineComponent,
  SetupContext,
  computed,
  ref,
  onMounted,
  watch,
  Ref,
} from 'vue'
import AddUser from '@/components/core/user/AddUser.vue'
import CardUser from '@/components/core/user/CardUser.vue'
import { Project } from '@/model/Project'
import { USER_ROLES } from '@/model/User'
import { User } from '@/model/User'
import { Toaster } from '@/common/Toaster'
import { __ } from '@/locales'
import { storeAuth, StoreGettersAuth } from '@/store/auth'
import {
  storeProject,
  StoreGettersProject,
  StoreActionsProject,
} from '@/store/project'
import { storeUser, StoreActionsUser } from '@/store/user'
import { ResetableComponent } from '@/model/ResetableComponent'

export default defineComponent({
  name: `ProjectTabUser`,
  components: {
    AddUser,
    CardUser,
  },
  props: {
    projectId: {
      type: Number,
      required: true,
    },
  },
  emits: ['validate', 'cancel'],
  setup(
    props: Readonly<{
      projectId: number
    }>,
    context: SetupContext,
  ) {
    const addUserEl: Ref<ResetableComponent> = ref(null)
    const loading = ref(false)
    const elementsPerPage = ref(12)
    const currentPage = ref(1)
    const loadingAddUser = ref(false)

    const isSuperadmin = computed(
      () => storeAuth.getters[StoreGettersAuth.ROLE] === USER_ROLES.SUPERADMIN,
    )
    const users = computed(
      (): User[] => storeProject.getters[StoreGettersProject.USERS],
    )
    const canAddUser = computed(
      () => storeAuth.getters[StoreGettersAuth.ROLE] === USER_ROLES.SUPERADMIN,
    )
    const project = computed(
      (): Project => storeProject.getters[StoreGettersProject.PROJECT],
    )

    watch(
      () => props.projectId,
      () => {
        if (!props.projectId) return
        if (props.projectId === project.value.id) return
        loadContent()
      },
    )

    onMounted(() => {
      loadContent()
    })

    const addUser = async (user: User) => {
      loadingAddUser.value = true
      const res = await storeUser.dispatch(StoreActionsUser.ADD, user)
      Toaster.toast(res)
      loadingAddUser.value = false
      if (res.success) {
        await storeProject.dispatch(StoreActionsProject.ADD_USER, res.data)
        addUserEl.value.reset()
      }
    }

    const loadContent = async () => {
      loading.value = true
      const res = storeProject.dispatch(
        StoreActionsProject.GET_USERS,
        props.projectId,
      )
      res.then(() => (loading.value = false))
    }

    const unlinkUser = async (id: number) => {
      const res = await storeProject.dispatch(StoreActionsProject.UNLINK_USER, {
        userId: id,
        projectId: props.projectId,
      })
      Toaster.toast(res)
      if (res.success) {
        storeProject.dispatch(StoreActionsProject.DELETE_USER, id)
      }
    }

    const deleteUser = async (id: number) => {
      if (confirm(__('User.askDelete').toString())) {
        const res = await storeUser.dispatch(StoreActionsUser.DELETE, id)
        Toaster.toast(res)
        if (res.success) {
          storeProject.dispatch(StoreActionsProject.DELETE_USER, id)
        }
      }
    }

    return {
      loading,
      addUserEl,
      isSuperadmin,
      users,
      canAddUser,
      loadingAddUser,
      addUser,
      elementsPerPage,
      currentPage,
      unlinkUser,
      deleteUser,
    }
  },
})
